import { useEffect, useRef, useState } from 'react';
import { GameProps } from './types';
import { TouchControls } from './TouchControls';

type InvadersPhase = 'intro' | 'playing' | 'gameover' | 'win';

interface Invader {
  x: number;
  y: number;
  alive: boolean;
  type: number; // 0, 1, 2 for different point values
}

interface Bullet {
  x: number;
  y: number;
  vy: number;
  isPlayer: boolean;
}

interface Star {
  x: number;
  y: number;
  size: number;
  brightness: number;
  twinkleSpeed: number;
  twinkleOffset: number;
}

const TWO_PI = Math.PI * 2;

const createStars = (width: number, height: number, count: number): Star[] => {
  const stars: Star[] = [];
  for (let i = 0; i < count; i++) {
    stars.push({
      x: Math.random() * width,
      y: Math.random() * height,
      size: 0.8 + Math.random() * 1.2,
      brightness: 0.3 + Math.random() * 0.7,
      twinkleSpeed: 1 + Math.random() * 3,
      twinkleOffset: Math.random() * TWO_PI,
    });
  }
  return stars;
};

// 8-bit invader sprites (8x8)
const INVADER_SPRITES = [
  // Type 0 - squid
  [
    [0, 0, 0, 1, 1, 0, 0, 0],
    [0, 0, 1, 1, 1, 1, 0, 0],
    [0, 1, 1, 1, 1, 1, 1, 0],
    [1, 1, 0, 1, 1, 0, 1, 1],
    [1, 1, 1, 1, 1, 1, 1, 1],
    [0, 1, 0, 1, 1, 0, 1, 0],
    [1, 0, 0, 0, 0, 0, 0, 1],
    [0, 1, 0, 0, 0, 0, 1, 0],
  ],
  // Type 1 - crab
  [
    [0, 0, 1, 0, 0, 1, 0, 0],
    [0, 0, 0, 1, 1, 0, 0, 0],
    [0, 0, 1, 1, 1, 1, 0, 0],
    [0, 1, 1, 0, 0, 1, 1, 0],
    [1, 1, 1, 1, 1, 1, 1, 1],
    [1, 0, 1, 1, 1, 1, 0, 1],
    [1, 0, 1, 0, 0, 1, 0, 1],
    [0, 0, 0, 1, 1, 0, 0, 0],
  ],
  // Type 2 - octopus
  [
    [0, 0, 0, 1, 1, 0, 0, 0],
    [0, 0, 1, 1, 1, 1, 0, 0],
    [0, 1, 1, 1, 1, 1, 1, 0],
    [1, 1, 0, 1, 1, 0, 1, 1],
    [1, 1, 1, 1, 1, 1, 1, 1],
    [0, 0, 1, 0, 0, 1, 0, 0],
    [0, 1, 0, 1, 1, 0, 1, 0],
    [1, 0, 1, 0, 0, 1, 0, 1],
  ],
];

// Player ship sprite (11x8)
const PLAYER_SPRITE = [
  [0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0],
  [0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0],
  [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
];

const INVADER_COLS = 11;
const INVADER_ROWS = 5;
const INVADER_SIZE = 24;
const INVADER_SPACING = 12;
const PLAYER_WIDTH = 33;
const PLAYER_HEIGHT = 24;
const PLAYER_SPEED = 220;

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

const drawSprite = (
  ctx: CanvasRenderingContext2D,
  sprite: number[][],
  x: number,
  y: number,
  width: number,
  height: number,
  color: string
) => {
  const rows = sprite.length;
  const cols = sprite[0].length;
  const pw = width / cols;
  const ph = height / rows;
  ctx.fillStyle = color;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (sprite[r][c] === 1) {
        ctx.fillRect(Math.floor(x + c * pw), Math.floor(y + r * ph), Math.ceil(pw), Math.ceil(ph));
      }
    }
  }
};

export function SpaceInvadersGame({ fgColor, bgColor, softColor, onExit }: GameProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  const keysRef = useRef({ left: false, right: false, space: false });

  const stateRef = useRef({
    phase: 'intro' as InvadersPhase,
    width: 0,
    height: 0,
    deviceRatio: 1,
    playerX: 0,
    invaders: [] as Invader[],
    bullets: [] as Bullet[],
    stars: [] as Star[],
    invaderDir: 1,
    invaderSpeed: 30,
    invaderMoveTimer: 0,
    invaderShootTimer: 0,
    score: 0,
    lives: 3,
    lastTime: 0,
    shootCooldown: 0,
    level: 1,
    time: 0,
  });

  const createInvaders = () => {
    const state = stateRef.current;
    const invaders: Invader[] = [];
    const gridWidth = INVADER_COLS * (INVADER_SIZE + INVADER_SPACING);
    const startX = (state.width - gridWidth) / 2 + INVADER_SIZE / 2;
    const startY = 60;

    for (let row = 0; row < INVADER_ROWS; row++) {
      for (let col = 0; col < INVADER_COLS; col++) {
        invaders.push({
          x: startX + col * (INVADER_SIZE + INVADER_SPACING),
          y: startY + row * (INVADER_SIZE + INVADER_SPACING - 4),
          alive: true,
          type: row < 1 ? 0 : row < 3 ? 1 : 2,
        });
      }
    }
    return invaders;
  };

  const resetRound = () => {
    const state = stateRef.current;
    state.phase = 'intro';
    state.playerX = state.width / 2;
    state.invaders = createInvaders();
    state.bullets = [];
    state.invaderDir = 1;
    state.invaderSpeed = 30;
    state.invaderMoveTimer = 0;
    state.invaderShootTimer = 0;
    state.score = 0;
    state.lives = 3;
    state.lastTime = 0;
    state.shootCooldown = 0;
    state.level = 1;
  };

  const nextLevel = () => {
    const state = stateRef.current;
    state.level++;
    state.invaders = createInvaders();
    state.bullets = [];
    state.invaderDir = 1;
    state.invaderSpeed = 30 + state.level * 8;
    state.invaderMoveTimer = 0;
  };

  const resizeCanvas = () => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const bounds = container.getBoundingClientRect();
    const width = Math.max(320, Math.floor(bounds.width));
    const height = Math.max(240, Math.floor(bounds.height));
    const deviceRatio = window.devicePixelRatio || 1;

    canvas.width = Math.floor(width * deviceRatio);
    canvas.height = Math.floor(height * deviceRatio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const state = stateRef.current;
    state.width = width;
    state.height = height;
    state.deviceRatio = deviceRatio;
    state.stars = createStars(width, height, 60);

    resetRound();
  };

  const playerShoot = () => {
    const state = stateRef.current;
    if (state.shootCooldown > 0) return;
    if (state.bullets.filter((b) => b.isPlayer).length >= 2) return;

    state.bullets.push({
      x: state.playerX,
      y: state.height - 50,
      vy: -350,
      isPlayer: true,
    });
    state.shootCooldown = 0.4;
  };

  const invaderShoot = () => {
    const state = stateRef.current;
    const aliveInvaders = state.invaders.filter((inv) => inv.alive);
    if (aliveInvaders.length === 0) return;

    // Bottom-most invaders in each column shoot
    const columns: { [key: number]: Invader } = {};
    aliveInvaders.forEach((inv) => {
      const col = Math.round(inv.x / (INVADER_SIZE + INVADER_SPACING));
      if (!columns[col] || inv.y > columns[col].y) {
        columns[col] = inv;
      }
    });

    const shooters = Object.values(columns);
    const shooter = shooters[Math.floor(Math.random() * shooters.length)];

    state.bullets.push({
      x: shooter.x,
      y: shooter.y + INVADER_SIZE / 2,
      vy: 180 + state.level * 20,
      isPlayer: false,
    });
  };

  const update = (timestamp: number) => {
    const state = stateRef.current;
    if (!state.lastTime) state.lastTime = timestamp;

    const delta = clamp((timestamp - state.lastTime) / 1000, 0, 0.05);
    state.lastTime = timestamp;
    state.time += delta;

    if (state.phase !== 'playing') return;

    const keys = keysRef.current;

    // Player movement
    if (keys.left) state.playerX -= PLAYER_SPEED * delta;
    if (keys.right) state.playerX += PLAYER_SPEED * delta;
    state.playerX = clamp(state.playerX, PLAYER_WIDTH / 2 + 10, state.width - PLAYER_WIDTH / 2 - 10);

    // Shooting
    if (keys.space) playerShoot();
    state.shootCooldown = Math.max(0, state.shootCooldown - delta);

    // Invader movement
    state.invaderMoveTimer += delta;
    const aliveCount = state.invaders.filter((i) => i.alive).length;
    const moveInterval = Math.max(0.05, 0.5 - aliveCount * 0.008);

    if (state.invaderMoveTimer >= moveInterval) {
      state.invaderMoveTimer = 0;

      // Check if any invader hits edge
      let hitEdge = false;
      const aliveInvaders = state.invaders.filter((i) => i.alive);

      aliveInvaders.forEach((inv) => {
        if (
          (state.invaderDir > 0 && inv.x + INVADER_SIZE / 2 >= state.width - 20) ||
          (state.invaderDir < 0 && inv.x - INVADER_SIZE / 2 <= 20)
        ) {
          hitEdge = true;
        }
      });

      if (hitEdge) {
        state.invaderDir *= -1;
        state.invaders.forEach((inv) => {
          inv.y += 12;
        });
      } else {
        state.invaders.forEach((inv) => {
          inv.x += state.invaderDir * 10;
        });
      }
    }

    // Invader shooting
    state.invaderShootTimer += delta;
    if (state.invaderShootTimer >= 1.2 - state.level * 0.1) {
      state.invaderShootTimer = 0;
      invaderShoot();
    }

    // Update bullets
    state.bullets = state.bullets
      .map((b) => ({ ...b, y: b.y + b.vy * delta }))
      .filter((b) => b.y > -10 && b.y < state.height + 10);

    // Player bullet hits invader
    state.bullets = state.bullets.filter((bullet) => {
      if (!bullet.isPlayer) return true;

      const hitInvader = state.invaders.find(
        (inv) =>
          inv.alive &&
          Math.abs(bullet.x - inv.x) < INVADER_SIZE / 2 &&
          Math.abs(bullet.y - inv.y) < INVADER_SIZE / 2
      );

      if (hitInvader) {
        hitInvader.alive = false;
        state.score += (3 - hitInvader.type) * 10;
        return false;
      }
      return true;
    });

    // Enemy bullet hits player
    const playerHit = state.bullets.find(
      (b) =>
        !b.isPlayer &&
        Math.abs(b.x - state.playerX) < PLAYER_WIDTH / 2 &&
        b.y >= state.height - 50 &&
        b.y <= state.height - 30
    );

    if (playerHit) {
      state.bullets = state.bullets.filter((b) => b !== playerHit);
      state.lives--;
      if (state.lives <= 0) {
        state.phase = 'gameover';
        return;
      }
    }

    // Invaders reach bottom
    const reachedBottom = state.invaders.some(
      (inv) => inv.alive && inv.y + INVADER_SIZE / 2 >= state.height - 60
    );
    if (reachedBottom) {
      state.phase = 'gameover';
      return;
    }

    // All invaders dead - next level
    if (state.invaders.every((inv) => !inv.alive)) {
      nextLevel();
    }
  };

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const state = stateRef.current;
    const { width, height, deviceRatio } = state;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(deviceRatio, 0, 0, deviceRatio, 0, 0);

    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, width, height);

    // Draw stars with twinkling
    state.stars.forEach((star) => {
      const twinkle = Math.sin(state.time * star.twinkleSpeed + star.twinkleOffset) * 0.3 + 0.7;
      ctx.globalAlpha = star.brightness * twinkle * 0.6;
      ctx.fillStyle = fgColor;
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.size, 0, TWO_PI);
      ctx.fill();
    });
    ctx.globalAlpha = 1;

    // HUD
    ctx.fillStyle = fgColor;
    ctx.font = '16px "Courier New", monospace';
    ctx.fillText(`INVADERS  SCORE ${state.score}  LIVES ${state.lives}  LEVEL ${state.level}`, 8, 22);

    // Draw invaders
    state.invaders.forEach((inv) => {
      if (!inv.alive) return;
      const sprite = INVADER_SPRITES[inv.type];
      drawSprite(
        ctx,
        sprite,
        inv.x - INVADER_SIZE / 2,
        inv.y - INVADER_SIZE / 2,
        INVADER_SIZE,
        INVADER_SIZE,
        softColor
      );
    });

    // Draw player
    drawSprite(
      ctx,
      PLAYER_SPRITE,
      state.playerX - PLAYER_WIDTH / 2,
      height - 45,
      PLAYER_WIDTH,
      PLAYER_HEIGHT,
      fgColor
    );

    // Draw bullets
    state.bullets.forEach((b) => {
      ctx.fillStyle = b.isPlayer ? fgColor : softColor;
      ctx.fillRect(b.x - 2, b.y - 4, 4, 8);
    });

    // Ground line
    ctx.strokeStyle = fgColor;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, height - 18);
    ctx.lineTo(width, height - 18);
    ctx.stroke();

    if (state.phase === 'intro') {
      ctx.fillStyle = softColor;
      ctx.font = '15px "Courier New", monospace';
      ctx.fillText('Arrows to move, SPACE to shoot. Press ENTER to start.', 8, height - 32);
    }

    if (state.phase === 'gameover') {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = fgColor;
      ctx.font = '24px "Courier New", monospace';
      ctx.fillText('GAME OVER', Math.max(8, width / 2 - 72), height / 2 - 10);
      ctx.font = '16px "Courier New", monospace';
      ctx.fillStyle = softColor;
      ctx.fillText(`Score: ${state.score}`, Math.max(8, width / 2 - 50), height / 2 + 18);
      ctx.fillText('Press ENTER to restart or ESC to quit', Math.max(8, width / 2 - 158), height / 2 + 44);
    }
  };

  const handleTouchDirection = (dir: string) => {
    if (dir === 'left') keysRef.current.left = true;
    else if (dir === 'right') keysRef.current.right = true;

    const state = stateRef.current;
    if (state.phase === 'intro') {
      state.phase = 'playing';
      state.lastTime = 0;
    }
    if (state.phase === 'gameover') {
      resetRound();
      state.phase = 'playing';
    }

    setTimeout(() => {
      if (dir === 'left') keysRef.current.left = false;
      else if (dir === 'right') keysRef.current.right = false;
    }, 100);
  };

  const handleTouchAction = () => {
    const state = stateRef.current;
    if (state.phase === 'intro') {
      state.phase = 'playing';
      state.lastTime = 0;
      return;
    }
    if (state.phase === 'gameover') {
      resetRound();
      state.phase = 'playing';
      return;
    }
    playerShoot();
  };

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    resizeCanvas();

    const observer = new ResizeObserver(() => resizeCanvas());
    if (containerRef.current) observer.observe(containerRef.current);

    const tick = (ts: number) => {
      update(ts);
      draw();
      animationRef.current = requestAnimationFrame(tick);
    };
    animationRef.current = requestAnimationFrame(tick);

    const handleKeyDown = (e: KeyboardEvent) => {
      const state = stateRef.current;
      if (e.key === 'Escape') {
        e.preventDefault();
        onExit(String(state.score));
        return;
      }

      if (e.key === 'ArrowLeft') keysRef.current.left = true;
      if (e.key === 'ArrowRight') keysRef.current.right = true;
      if (e.key === ' ') {
        e.preventDefault();
        keysRef.current.space = true;
      }

      if (state.phase === 'intro' && e.key === 'Enter') {
        state.phase = 'playing';
        state.lastTime = 0;
      }
      if (state.phase === 'gameover' && e.key === 'Enter') {
        e.preventDefault();
        resetRound();
        state.phase = 'playing';
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') keysRef.current.left = false;
      if (e.key === 'ArrowRight') keysRef.current.right = false;
      if (e.key === ' ') keysRef.current.space = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      observer.disconnect();
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="game-canvas-wrap" ref={containerRef}>
      {isTouchDevice && <div className="game-touch-instruction">D-pad to move, tap FIRE to shoot.</div>}
      <canvas ref={canvasRef} className="game-canvas" />
      {isTouchDevice && (
        <TouchControls
          type="dpad"
          onDirection={handleTouchDirection}
          onAction={handleTouchAction}
          onQuit={() => onExit(String(stateRef.current.score))}
          actionLabel="FIRE"
        />
      )}
    </div>
  );
}
